'use client';
import {
	useState,
	createContext,
	useContext,
	useEffect,
	useCallback,
	SetStateAction,
	Dispatch,
} from 'react';
import { FormControlLabel, styled, TextFieldProps, FormHelperText } from '@mui/material';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { buttonSX } from '@/components/blocks/DpLibrary/DpButtonForm/style/main';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { DpCheckbox } from '@/components/blocks/DpCheckbox';

const Form = styled('form')({});

interface DpFormProps {
	id: string;
	action: string;
	name: string;
	method: string;
	children: React.ReactNode;
}

interface DpFormContextType {
	formId: string;
	formAction: string;
	formMethod: string;
	formData: Record<string, unknown>;
	setFormData: Dispatch<SetStateAction<Record<string, unknown>>>;
	submitting: boolean;
	setSubmitting: (submitting: boolean) => void;
	formRegex: Record<string, string>;
	formRequired: Record<string, boolean>;
	setFormRequired: Dispatch<SetStateAction<Record<string, boolean>>>;
	setFormRegex: Dispatch<SetStateAction<Record<string, string>>>;
	formErrors: Record<string, boolean>;
	setFormErrors: Dispatch<SetStateAction<Record<string, boolean>>>;
}

export const DpFormContext = createContext<DpFormContextType>({
	formData: {},
	setFormData: () => null,
	formId: '',
	formAction: '',
	formMethod: '',
	submitting: false,
	setSubmitting: () => null,
	formRegex: {},
	setFormRegex: () => null,
	formErrors: {},
	setFormErrors: () => null,
	formRequired: {},
	setFormRequired: () => null,
});

export const DpForm = ({ children, action, method, ...props }: DpFormProps) => {
	const [formData, setFormData] = useState<Record<string, unknown>>({});
	const [submitting, setSubmitting] = useState(false);
	const [formRegex, setFormRegex] = useState<Record<string, string>>({});
	const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
	const [formRequired, setFormRequired] = useState<Record<string, boolean>>({});

	return (
		<DpFormContext.Provider
			value={{
				formData,
				setFormData,
				formId: props?.id || '',
				formAction: action || '',
				formMethod: method || '',
				submitting,
				setSubmitting,
				formRegex,
				setFormRegex,
				formErrors,
				setFormErrors,
				formRequired,
				setFormRequired,
			}}
		>
			<Form action={action} method={method} {...props}>
				{children}
			</Form>
		</DpFormContext.Provider>
	);
};

type DpFormInputProps = TextFieldProps & {
	fullwidth?: boolean;
	regex?: string;
	maxlength?: number;
	formatdate?: 'dd/mm/yyyy' | 'dd-mm-yyyy';
};

export const DpFormInputHidden = ({ name, value }: { name: string; value: string }) => {
	const { setFormData } = useContext(DpFormContext);

	useEffect(() => {
		setFormData((prev) => ({ ...prev, [name]: value }));
	}, [name, value, setFormData]);

	return null;
};

const formatDate = (date: string, format: 'dd/mm/yyyy' | 'dd-mm-yyyy' = 'dd/mm/yyyy') => {
	if (!date || date.trim() === '') {
		return '';
	}

	const numbersOnly = date.replace(/\D/g, '');

	if (numbersOnly.length === 0) {
		return '';
	}

	let formatted = '';
	const separator = format === 'dd-mm-yyyy' ? '-' : '/';

	if (numbersOnly.length <= 2) {
		formatted = numbersOnly;
	} else if (numbersOnly.length <= 4) {
		const day = numbersOnly.substring(0, 2);
		const month = numbersOnly.substring(2);
		formatted = `${day}${separator}${month}`;
	} else {
		const day = numbersOnly.substring(0, 2);
		const month = numbersOnly.substring(2, 4);
		const year = numbersOnly.substring(4, 8);
		formatted = `${day}${separator}${month}${separator}${year}`;
	}

	return formatted;
};

export const DpFormInput = ({
	fullwidth,
	regex,
	error,
	type,
	formatdate,
	...props
}: DpFormInputProps) => {
	const { formData, setFormData, setFormRegex, formErrors, setFormErrors } =
		useContext(DpFormContext);

	useEffect(() => {
		if (regex) {
			setFormRegex((prev) => ({ ...prev, [props?.name ?? 'unknown']: regex }));
		}
	}, [regex, props.name, setFormRegex]);

	const isDate = type === 'date';

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;
			let formattedValue = value;

			if (isDate) {
				formattedValue = formatDate(value, formatdate);
			}

			setFormData({ ...formData, [name]: formattedValue });
			setFormErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		},
		[formData, setFormData, setFormErrors, isDate, formatdate]
	);

	return (
		<DpOutlinedInput
			value={formData[props?.name ?? 'unknown']}
			error={!!formErrors[props?.name ?? 'unknown']}
			helperText={formErrors[props?.name ?? 'unknown'] ? error : ''}
			fullWidth={fullwidth}
			type={isDate ? 'text' : type}
			inputProps={{
				maxLength: props?.maxlength,
			}}
			{...props}
			onChange={handleChange}
		/>
	);
};

type DpFormButtonProps = {
	label: string;
};

export const dpExecuteFormAction = async (
	formData: Record<string, unknown>,
	formAction: string,
	formMethod: string
) => {
	const form = document.createElement('form');
	form.method = formMethod;
	form.action = formAction;
	form.style.display = 'none';

	Object.entries(formData).forEach(([key, value]) => {
		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = key;
		input.value = typeof value === 'string' ? value : JSON.stringify(value);
		form.appendChild(input);
	});

	document.body.appendChild(form);

	setTimeout(() => {
		form.submit();
	}, 500); // 0.5 segundos

	setTimeout(() => {
		if (document.body.contains(form)) {
			document.body.removeChild(form);
		}
	}, 30000); // 30 segundos
};

export const DpFormButton = ({ label, ...props }: DpFormButtonProps) => {
	const {
		formData,
		formAction,
		formMethod,
		setSubmitting,
		submitting,
		formRegex,
		setFormErrors,
		formRequired,
	} = useContext(DpFormContext);

	const handleClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			if (Object.keys(formData).length === 0) {
				return;
			}

			const errors: Record<string, boolean> = {};

			Object.entries(formRegex).forEach(([key, _regex]) => {
				const value = (formData[key] ?? '') as string;
				const regex = new RegExp(_regex);
				if (!regex.test(value)) {
					errors[key] = true;
				}
			});

			Object.entries(formRequired).forEach(([key, value]) => {
				if (value && !formData[key]) {
					errors[key] = true;
				}
			});

			if (Object.keys(errors).length > 0) {
				setFormErrors(errors);
				return;
			} else {
				setFormErrors({});
			}

			try {
				await dpExecuteFormAction(formData, formAction, formMethod);
			} catch (error) {
				console.error(error);
			} finally {
				setSubmitting(false);
			}
		},
		[formData, formAction, formMethod, setSubmitting, formRegex, setFormErrors, formRequired]
	);

	return (
		<DpButton
			type="button"
			fullWidth
			sx={buttonSX}
			disabled={submitting}
			onClick={handleClick}
			{...props}
		>
			{label}
		</DpButton>
	);
};

interface DpCheckboxFormProps {
	name: string;
	children: React.ReactNode;
	required?: boolean;
	error?: string;
}

export const DpCheckboxForm = ({ required, children, error, ...props }: DpCheckboxFormProps) => {
	const { formData, setFormData, setFormRequired, formErrors, setFormErrors } =
		useContext(DpFormContext);

	useEffect(() => {
		if (required) {
			setFormRequired((prev) => ({ ...prev, [props.name]: true }));
		}
	}, [required, props.name, setFormRequired]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, checked } = event.target;
			setFormData({ ...formData, [name]: checked });
			setFormErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		},
		[formData, setFormData, setFormErrors]
	);

	return (
		<>
			<FormControlLabel
				control={
					<DpCheckbox
						name={props.name}
						checked={formData[props.name] as boolean}
						inputProps={{ 'aria-label': 'controlled', disabled: false }}
						disabled={false}
						onChange={handleChange}
					/>
				}
				label={children}
			/>
			{!!formErrors[props?.name ?? 'unknown'] ? (
				<FormHelperText
					sx={{
						fontSize: 'var(--font-size-caption)',
						lineHeight: '1',
						height: '20px',
						color: '#d32f2f',
					}}
				>
					{error || 'Este campo es requerido'}
				</FormHelperText>
			) : null}
		</>
	);
};
